import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import apiClient from "../../../../services/apiClient";
import { setSection, updateSection } from "../../assignmentSlice";
import PassageAudioButton from "../widgets/PassageAudioButton";
import PageFieldLayer from "./PageFieldLayer";
import SentenceMicLayer from "./SentenceMicLayer";
import SpeakingPanel, { MicIcon } from "./SpeakingPanel";
import "./EbookPage.css";

// 리더스북 전자책(지인에듀, 2026-10-17). 종이책과 같은 쪽 그림을 넘겨 보고, 본문 쪽에서는 지문
// 음원을, 듣기 쪽에서는 듣기 음원을 바로 튼다. 쪽 차례와 그림 · 음원 주소는 출제 때 서버가
// 만들어 둔다(api/assignment/readers_book.py).
//
// 답 칸이 있는 쪽(지인에듀 fields, version 2)은 아이가 쪽 위에 바로 쓰고 '채점하기' 를 누른다.
// 서버가 그 쪽을 매기고(readers_grading.py) 같은 자리에 빨간 펜 첨삭을 얹는다. 고친 이유는
// ①②③ 번호를 달아 '첨삭 쪽지' 에 적는다. 한 번 매긴 쪽은 다시 쓰지 못한다.
//
// 넓은 가로 화면에서는 종이책처럼 두 쪽(짝수-홀수)을 펼쳐 놓는다.
// 저장: Section.answer 는 [{type: "ebook_state", last_page, viewed_pages, responses, results,
// graded_pages}] 한 줄이다. 다음에 들어오면 마지막으로 본 쪽부터 연다.

const STATE_TYPE = "ebook_state";
// 책 화면은 머리줄 없이 화면 전체를 쓴다(bookScreen.js, StudyPage.css 의 study-page__column--book)
export const BOOK_SCREEN_HEIGHT = "calc(100vh - var(--dev-bar-height))";
const ACCENT = "#D14D72";
const SWIPE_DISTANCE = 50;
const RESPONSE_SAVE_DELAY = 1500;
const CIRCLED_NUMBER = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";

const isNarrowScreen = () =>
  typeof window !== "undefined" && Math.min(window.innerWidth, window.screen.width) <= 768;

const isSpreadScreen = () =>
  typeof window !== "undefined" &&
  window.innerWidth >= 1100 &&
  window.innerWidth > window.innerHeight * 1.2;

// 펼침면 목록. 각 항목은 쪽 차례의 순번(0부터) 배열이다. 종이책처럼 짝수 쪽(왼쪽)과 바로 다음 홀수 쪽(오른쪽)을
// 펼친다. 섹션이 유닛 · 문제로 나뉘어(2026-10-18) 홀수 쪽으로 시작하는 섹션은 그 쪽을 혼자 둔다.
const buildSpreadList = (pageList, isSpread) => {
  if (!isSpread) return pageList.map((_, index) => [index]);
  const spreadList = [];
  for (let index = 0; index < pageList.length; index += 1) {
    const page = pageList[index];
    const next = pageList[index + 1];
    if (next && page.page % 2 === 0 && next.page === page.page + 1) {
      spreadList.push([index, index + 1]);
      index += 1;
    } else {
      spreadList.push([index]);
    }
  }
  return spreadList;
};

const readSavedState = (answerList) => {
  const saved = (answerList || []).find((one) => one && one.type === STATE_TYPE) || {};
  return {
    lastPage: Number.isInteger(saved.last_page) ? saved.last_page : null,
    viewedPages: saved.viewed_pages || [],
    responses: saved.responses || {},
    results: saved.results || {},
    gradedPages: saved.graded_pages || [],
    // 따라 말하기: {음원 주소: {문장 idx: {first_accuracy, last_accuracy, spoken, attempt_count}}}
    speaking: saved.speaking || {},
  };
};

const EbookPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const question = section?.question || {};
  const book = question.book || {};
  const accent = book.theme_color || ACCENT;

  const savedState = useMemo(() => readSavedState(section?.answer), [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pageList, setPageList] = useState(() => question.pages || []);
  const [isMobile, setIsMobile] = useState(isNarrowScreen);
  const [isSpread, setIsSpread] = useState(isSpreadScreen);
  const [failedPages, setFailedPages] = useState([]);
  const [responses, setResponses] = useState(savedState.responses);
  const [results, setResults] = useState(savedState.results);
  const [gradedPages, setGradedPages] = useState(savedState.gradedPages);
  const [gradingPages, setGradingPages] = useState([]);
  const [isMemoOpen, setIsMemoOpen] = useState(false);
  const [speaking, setSpeaking] = useState(savedState.speaking);
  const [isSpeakingOpen, setIsSpeakingOpen] = useState(false);

  const spreadList = useMemo(() => buildSpreadList(pageList, isSpread), [pageList, isSpread]);
  const viewedRef = useRef(new Set(savedState.viewedPages));
  // 지금 펼친 면을 쪽 번호로 기억한다. 화면을 돌려 한 쪽 ↔ 두 쪽 보기가 바뀌어도 같은 쪽에 머문다.
  const [anchorPage, setAnchorPage] = useState(() => {
    const first = (question.pages || [])[0];
    return savedState.lastPage ?? (first ? first.page : 1);
  });

  // 섹션이 바뀌면(다른 책) 처음부터 다시 잡는다. section 이 아니라 section.id 에 건다.
  // 저장할 때마다 updateSection 이 state.section 을 통째로 바꾸므로 section 에 걸면 쪽이 튄다.
  useEffect(() => {
    setPageList(section?.question?.pages || []);
  }, [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isNarrowScreen());
      setIsSpread(isSpreadScreen());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const indexOfPage = (pageNumber) => pageList.findIndex((page) => page.page === pageNumber);
  const currentSpreadIndex = Math.max(
    0,
    spreadList.findIndex((spread) => spread.includes(Math.max(0, indexOfPage(anchorPage))))
  );
  const currentSpread = spreadList[currentSpreadIndex] || [];
  const currentPageList = currentSpread.map((index) => pageList[index]).filter(Boolean);
  const isLastSpread = currentSpreadIndex >= spreadList.length - 1;

  // 펼친 면의 음원을 쪽마다. 두 쪽이 같은 지문 음원을 달고 있으면 앞쪽에만 둔다.
  const audioListByPage = useMemo(() => {
    const seen = new Set();
    const byPage = {};
    currentPageList.forEach((page) => {
      byPage[page.page] = (page.audio_list || []).filter((audio) => {
        if (!audio.url || seen.has(audio.url)) return false;
        seen.add(audio.url);
        return true;
      });
    });
    return byPage;
  }, [currentPageList]);

  // 첨삭 쪽지에 적을 것: 펼친 면에서 이유가 달린 칸. 쪽 위의 ①②③ 과 같은 번호다.
  const noteList = useMemo(() => {
    const list = [];
    currentPageList.forEach((page) => {
      (page.fields || []).forEach((field) => {
        const result = results[field.id];
        if (result && result.reason) {
          list.push({ id: field.id, verdict: result.verdict, reason: result.reason });
        }
      });
    });
    return list.map((note, index) => ({ ...note, number: CIRCLED_NUMBER[index] || `${index + 1}` }));
  }, [currentPageList, results]);
  const noteNumberById = useMemo(
    () => Object.fromEntries(noteList.map((note) => [note.id, note.number])),
    [noteList]
  );

  // 본문 쪽이면 그 음원의 문장으로 따라 말하기를 연다(듣기 활동 쪽은 문제를 풀려고 듣는 것이라 열지 않는다)
  // 선생님이 출제할 때 '따라 말하기' 를 켠 과제만 연다. 이 칸이 없는 것(10/17 처음 낸 과제)은 켜진 것으로 본다.
  const isSpeakingEnabled = question.speaking_enabled !== false;
  const speakingAudio = useMemo(() => {
    if (!isSpeakingEnabled) return null;
    for (const page of currentPageList) {
      if (page.kind !== "passage") continue;
      const audio = (page.audio_list || []).find((one) => (one.sentences || []).length > 0);
      if (audio) return audio;
    }
    return null;
  }, [currentPageList, isSpeakingEnabled]);

  // 지인에듀가 문장 끝 자리(sentences[].end)를 준 본문이면 쪽 위 문장 끝에 마이크를 단다(SentenceMicLayer).
  // 그때는 '따라 말하기' 목록 단추를 감춘다. 자리가 없는 책은 지금처럼 목록으로 연다.
  const passageAudioList = useMemo(() => {
    if (!isSpeakingEnabled) return [];
    const seen = new Set();
    return currentPageList
      .filter((page) => page.kind === "passage")
      .flatMap((page) => page.audio_list || [])
      .filter((audio) => {
        if (!audio.url || seen.has(audio.url) || !(audio.sentences || []).length) return false;
        seen.add(audio.url);
        return true;
      });
  }, [currentPageList, isSpeakingEnabled]);
  const hasSentenceMarks = passageAudioList.some((audio) => audio.sentences.some((sentence) => sentence.end));

  const stateRef = useRef({ responses, results, gradedPages, speaking });
  stateRef.current = { responses, results, gradedPages, speaking };

  const save = useCallback(
    (lastPage, isFinished) => {
      if (isPreview || isReview) return;
      const current = stateRef.current;
      const answer = [
        {
          type: STATE_TYPE,
          last_page: lastPage,
          viewed_pages: Array.from(viewedRef.current).sort((a, b) => a - b),
          responses: current.responses,
          results: current.results,
          graded_pages: current.gradedPages,
          speaking: current.speaking,
        },
      ];
      dispatch(setSection({ ...section, answer }));
      dispatch(
        updateSection({
          sectionId: section.id,
          data: isFinished ? { answer, is_finished: true } : { answer },
        })
      );
    },
    [dispatch, section, isPreview, isReview]
  );

  // 면을 넘길 때마다 본 쪽을 적어 두고 저장한다. 다음에 들어오면 여기서부터 연다.
  const currentPageKey = currentPageList.map((page) => page.page).join(",");
  useEffect(() => {
    if (!currentPageList.length) return;
    currentPageList.forEach((page) => viewedRef.current.add(page.page));
    save(currentPageList[0].page, false);
    setIsMemoOpen(false);
    setIsSpeakingOpen(false);
  }, [currentPageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 쓰는 중인 답은 잠깐 멈추면 저장한다. 채점 전에 나가도 쓴 것이 남는다.
  const saveTimerRef = useRef(null);
  const handleResponseChange = (fieldId, value) => {
    setResponses((previous) => ({ ...previous, [fieldId]: value }));
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => save(currentPageList[0]?.page ?? anchorPage, false), RESPONSE_SAVE_DELAY);
  };
  useEffect(() => () => clearTimeout(saveTimerRef.current), []);
  // 칸을 떠나면(엔터 · 다른 곳 누르기) 기다리지 않고 바로 저장한다
  const commitResponses = () => {
    clearTimeout(saveTimerRef.current);
    save(currentPageList[0]?.page ?? anchorPage, false);
  };

  // 출제 때 못 받은 쪽 그림은 열 때 서버가 받아 채운다.
  useEffect(() => {
    currentPageList
      .filter((page) => !page.image && !failedPages.includes(page.page))
      .forEach((page) => {
        apiClient
          .get(`/api/v1/readers-book/page?section_id=${section.id}&page=${page.page}`)
          .then((response) => {
            const image = response.data?.image;
            if (!image) throw new Error("no image");
            setPageList((previous) =>
              previous.map((one) => (one.page === page.page ? { ...one, image } : one))
            );
          })
          .catch(() => setFailedPages((previous) => [...previous, page.page]));
      });
  }, [currentPageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 다음 면 그림을 미리 받아 둔다. 넘기는 순간 하얀 화면이 보이지 않게.
  useEffect(() => {
    const nextSpread = spreadList[currentSpreadIndex + 1] || [];
    nextSpread.forEach((index) => {
      const image = pageList[index]?.image;
      if (image) new Image().src = image;
    });
  }, [currentSpreadIndex, spreadList, pageList]);

  const handleSpeakingResult = (audioKey, sentenceIndex, accuracy, spokenText) => {
    const audioMap = stateRef.current.speaking[audioKey] || {};
    const previous = audioMap[sentenceIndex] || {};
    const nextSpeaking = {
      ...stateRef.current.speaking,
      [audioKey]: {
        ...audioMap,
        [sentenceIndex]: {
          // 점수는 첫 시도로 센다(calculate_grade). 화면과 해설지는 가장 잘 읽은 점수를 보인다.
          first_accuracy: previous.first_accuracy ?? accuracy,
          last_accuracy: accuracy,
          best_accuracy: Math.max(previous.best_accuracy ?? previous.last_accuracy ?? 0, accuracy),
          spoken: spokenText.slice(0, 300),
          attempt_count: (previous.attempt_count || 0) + 1,
        },
      },
    };
    stateRef.current = { ...stateRef.current, speaking: nextSpeaking };
    setSpeaking(nextSpeaking);
    save(currentPageList[0]?.page ?? anchorPage, false);
  };

  const ungradedPageList = currentPageList.filter(
    (page) => (page.fields || []).length > 0 && !gradedPages.includes(page.page)
  );

  const gradePages = async () => {
    const targetList = ungradedPageList.filter((page) => !gradingPages.includes(page.page));
    if (!targetList.length) return;
    setGradingPages((previous) => [...previous, ...targetList.map((page) => page.page)]);
    for (const page of targetList) {
      const pageResponses = Object.fromEntries(
        (page.fields || []).filter((field) => field.id in responses).map((field) => [field.id, responses[field.id]])
      );
      try {
        const response = await apiClient.post("/api/v1/readers-book/grade", {
          section_id: section.id,
          page: page.page,
          responses: pageResponses,
          preview: Boolean(isPreview),
        });
        const pageResults = response.data?.results || {};
        const isPending = Object.values(pageResults).some((one) => one && one.is_pending);
        stateRef.current = {
          ...stateRef.current,
          results: { ...stateRef.current.results, ...pageResults },
          gradedPages: isPending ? stateRef.current.gradedPages : [...stateRef.current.gradedPages, page.page],
        };
        setResults(stateRef.current.results);
        setGradedPages(stateRef.current.gradedPages);
        if (isPending) alert("채점이 잠깐 밀렸어요. 조금 뒤에 다시 눌러 주세요.");
      } catch (error) {
        alert("채점하지 못했어요. 다시 눌러 주세요.");
      }
    }
    setGradingPages((previous) => previous.filter((one) => !targetList.some((page) => page.page === one)));
    setIsMemoOpen(true);
  };

  const pageScoreOf = (page) => {
    const fieldList = page.fields || [];
    if (!fieldList.length || !gradedPages.includes(page.page)) return null;
    // 선 잇기는 쌍마다 하나로 센다(다섯 쌍이면 5점 만점). 나머지 칸은 칸마다 하나.
    let right = 0;
    let total = 0;
    fieldList.forEach((field) => {
      const result = results[field.id] || {};
      if (field.type === "match" && result.total_count) {
        right += result.right_count || 0;
        total += result.total_count;
      } else {
        right += result.score ?? 0;
        total += 1;
      }
    });
    return `${Number.isInteger(right) ? right : right.toFixed(1)} / ${total}`;
  };

  const goTo = (spreadIndex) => {
    const target = spreadList[Math.min(Math.max(spreadIndex, 0), spreadList.length - 1)];
    if (target && pageList[target[0]]) setAnchorPage(pageList[target[0]].page);
  };
  const goPrevious = () => goTo(currentSpreadIndex - 1);
  const goNext = () => goTo(currentSpreadIndex + 1);

  // 책 안의 '나가기'. 답은 쓰는 대로 저장되므로 이름에 저장을 넣지 않는다. 나갈 때도 한 번 더 저장하고, 다 끝낸 것으로 치지 않고 섹션 목록으로 돌아간다(끝내기는 '다 읽었어요').
  const handleExit = () => {
    clearTimeout(saveTimerRef.current);
    save(currentPageList[0]?.page ?? anchorPage, false);
    setCurrentStep(2);
  };

  const handleFinish = () => {
    if (isReview) {
      setCurrentStep(2);
      return;
    }
    const remainingCount = pageList.filter(
      (page) => (page.fields || []).length > 0 && !gradedPages.includes(page.page)
    ).length;
    if (remainingCount > 0 && !window.confirm(`아직 채점하지 않은 쪽이 ${remainingCount}쪽 있어요. 그래도 끝낼까요?`)) {
      return;
    }
    save(currentPageList[0]?.page ?? anchorPage, true);
    alert("학습이 완료되었습니다.");
    setCurrentStep(2);
  };

  useEffect(() => {
    const handleKey = (event) => {
      // 답을 쓰는 중에는 화살표가 글자 사이를 오가야 한다. 쪽을 넘기지 않는다.
      if (["INPUT", "TEXTAREA"].includes(event.target?.tagName)) return;
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  const touchStartRef = useRef(null);
  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };
  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const distanceX = touch.clientX - start.x;
    const distanceY = touch.clientY - start.y;
    if (Math.abs(distanceX) < SWIPE_DISTANCE || Math.abs(distanceX) < Math.abs(distanceY)) return;
    if (distanceX < 0) goNext();
    else goPrevious();
  };

  const retryPage = (pageNumber) => setFailedPages((previous) => previous.filter((one) => one !== pageNumber));

  if (!pageList.length) {
    return <div className="ebook__empty">책을 불러오지 못했어요.</div>;
  }

  const pageLabel = currentPageList.map((page) => page.page).join("-");
  const isGrading = currentPageList.some((page) => gradingPages.includes(page.page));
  const canWrite = !isReview;

  // 넘김 단추. 폰에서는 쪽 위에 얹으면 글을 가려서 아래 줄로 내린다(넘기기는 밀어서도 된다).
  const previousButton = (
    <button
      type="button"
      className="ebook__turn"
      onClick={goPrevious}
      disabled={currentSpreadIndex === 0}
      aria-label="앞 쪽"
    >
      ‹
    </button>
  );
  const nextButton = (
    <button type="button" className="ebook__turn" onClick={goNext} disabled={isLastSpread} aria-label="다음 쪽">
      ›
    </button>
  );

  return (
    <div className="ebook" style={{ height: BOOK_SCREEN_HEIGHT, "--ebook-accent": accent }}>
      <div className="ebook__bar">
        <div className="ebook__title">{book.title}</div>
        <div className="ebook__audio-list">
          {speakingAudio && !hasSentenceMarks && (
            <button type="button" className="ebook__speaking-toggle" onClick={() => setIsSpeakingOpen(!isSpeakingOpen)}>
              <MicIcon size={13} /> 따라 말하기
            </button>
          )}
          {noteList.length > 0 && (
            <button type="button" className="ebook__memo-toggle" onClick={() => setIsMemoOpen(!isMemoOpen)}>
              첨삭 쪽지 {noteList.length}
            </button>
          )}
          <button type="button" className="ebook__exit" onClick={handleExit}>
            나가기
          </button>
        </div>
      </div>

      <div className="ebook__stage" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {!isMobile && previousButton}
        <div className={`ebook__spread${currentPageList.length > 1 ? " ebook__spread--double" : ""}`}>
          {currentPageList.map((page) => (
            <div className="ebook__page" key={page.page}>
              {page.image ? (
                <img src={page.image} alt={`${page.page}쪽`} draggable={false} />
              ) : failedPages.includes(page.page) ? (
                <div className="ebook__page-message">
                  이 쪽을 불러오지 못했어요.
                  <button type="button" onClick={() => retryPage(page.page)}>
                    다시 불러오기
                  </button>
                </div>
              ) : (
                <div className="ebook__page-message">불러오는 중...</div>
              )}
              {page.image && (page.fields || []).length > 0 && (
                <PageFieldLayer
                  fieldList={page.fields}
                  responses={responses}
                  results={gradedPages.includes(page.page) ? results : {}}
                  onChange={handleResponseChange}
                  onCommit={commitResponses}
                  isEditable={canWrite && !gradedPages.includes(page.page) && !gradingPages.includes(page.page)}
                  noteNumberById={noteNumberById}
                />
              )}
              {pageScoreOf(page) && <div className="ebook__page-score">{pageScoreOf(page)}</div>}
              {page.image && page.kind === "passage" && hasSentenceMarks && (
                <SentenceMicLayer
                  pageNumber={page.page}
                  audioList={passageAudioList}
                  speaking={speaking}
                  onResult={handleSpeakingResult}
                  isEditable={!isReview}
                />
              )}
              {/* 전체 듣기는 쪽 위에 둔다. 펼친 두 쪽이 같은 음원이면 앞쪽에만. */}
              {page.image && (audioListByPage[page.page] || []).length > 0 && (
                <div className="ebook__page-audio">
                  {audioListByPage[page.page].map((audio) => (
                    <PassageAudioButton key={audio.url} audioUrl={audio.url} label={audio.label} accentColor={accent} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {!isMobile && nextButton}

        {isSpeakingOpen && speakingAudio && (
          <SpeakingPanel
            audio={speakingAudio}
            speakingMap={speaking[speakingAudio.url] || {}}
            onResult={(sentenceIndex, accuracy, spokenText) =>
              handleSpeakingResult(speakingAudio.url, sentenceIndex, accuracy, spokenText)
            }
            isEditable={!isReview}
            onClose={() => setIsSpeakingOpen(false)}
          />
        )}

        {isMemoOpen && noteList.length > 0 && (
          <div className="ebook__memo">
            <button type="button" className="ebook__memo-close" onClick={() => setIsMemoOpen(false)} aria-label="닫기">
              ×
            </button>
            <div className="ebook__memo-title">선생님 첨삭</div>
            {noteList.map((note) => (
              <div className="ebook__memo-line" key={note.id}>
                <span className="ebook__memo-number">{note.number}</span>
                <div>{note.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ebook__foot">
        {isMobile && previousButton}
        <div className="ebook__progress">
          <div style={{ width: `${((currentSpreadIndex + 1) / spreadList.length) * 100}%` }} />
        </div>
        <span className="ebook__counter">
          {pageLabel} / {pageList[pageList.length - 1].page}
        </span>
        {canWrite && ungradedPageList.length > 0 && (
          <button type="button" className="ebook__grade" onClick={gradePages} disabled={isGrading}>
            {isGrading ? "채점하는 중..." : "채점하기"}
          </button>
        )}
        {isLastSpread && (
          <button type="button" className="ebook__finish" onClick={handleFinish}>
            {isReview ? "나가기" : question.paper_source ? "다 풀었어요" : "다 읽었어요"}
          </button>
        )}
        {isMobile && !isLastSpread && nextButton}
      </div>

    </div>
  );
};

export default EbookPage;
